import apiClient from './client'

/**
 * Admin integration on/off settings. Backed by the backend
 * IntegrationSettingsController (GET/PUT /api/v1/integrations*).
 */
/**
 * MAPFRE's admin-editable configuration, as reported by the backend.
 *
 * `source` says whether the EFFECTIVE value came from the admin screen or from
 * the environment, which is what makes the DB -> env priority visible instead
 * of a thing you have to know.
 */
export interface IntegrationConfigField {
  value: string | number | null
  source: 'admin' | 'env'
}

/**
 * Secrets report only whether one IS SET and where from. The backend never
 * returns the value — there is no field here to hold one on purpose.
 */
export interface IntegrationConfigSecret {
  set: boolean
  source: 'admin' | 'env' | 'unset'
}

/** Which MAPFRE book the configured hosts point at (backend: MapfreConfig::environment). */
export type MapfreEnvironment = 'pre' | 'production' | 'unset'

export interface IntegrationConfig {
  fields: Record<string, IntegrationConfigField>
  secrets: Record<string, IntegrationConfigSecret>
  environment?: MapfreEnvironment
}

export interface Integration {
  integration: string          // slug, e.g. 'swiftly'
  label: string                // human label, e.g. 'Swiftly Finance'
  enabled: boolean
  configured: boolean          // true when the integration's credentials are present
  missing_config?: string[] | null  // which required keys are absent (managers only)
  program_id: string | null    // non-secret identifier, editable from the UI
  updated_by: string | null    // email/name of the last person who toggled it
  updated_at: string | null    // ISO timestamp of the last change
  notes: string | null
  // Present for integrations whose whole config set is admin-editable (MAPFRE),
  // and only for managers — null for everyone else, like missing_config above.
  config?: IntegrationConfig | null
  // Claims registration guard only: its checks and day limit (admin-editable).
  guard_settings?: { check_kyc: boolean; check_preinspection: boolean; check_unpaid: boolean; unpaid_days: number } | null
  // Renewal expiry digest only: comma-separated recipients per list (managers only).
  digest_settings?: { underwriting_emails: string; manager_emails: string; exco_emails: string } | null
}

export interface IntegrationsResponse {
  data: Integration[]
  can_manage: boolean          // whether the current user may toggle integrations
}

export interface UpdateIntegrationResult {
  integration: string
  enabled: boolean
  previous: boolean | null
  updated_by: string | null
  updated_at: string
}

export async function getIntegrations(): Promise<IntegrationsResponse> {
  const res = await apiClient.get('/integrations')
  return res.data
}

export async function updateIntegration(
  slug: string,
  data: { enabled: boolean; notes?: string },
): Promise<{ success: boolean; message: string; data: UpdateIntegrationResult }> {
  const res = await apiClient.put(`/integrations/${slug}`, data)
  return res.data
}

export interface TestConnectionResult {
  ok: boolean
  http_status?: number | null
  // Swiftly: read back from GET /offtaker/suppliers.
  program_id?: string | null
  supplier_count?: number
  suppliers?: unknown[]
  raw_preview?: string | null
  // MAPFRE: the Cognito -> eMiA auth chain carries no supplier list, so it
  // reports which dealer/environment it authenticated against instead — the
  // usual reason to run the test (the PRE dealer is IT-only, production BW).
  message?: string
  base_url?: string | null
  country_id?: string | null
  environment?: MapfreEnvironment
  error?: string
}

export async function testIntegrationConnection(slug: string): Promise<TestConnectionResult> {
  const res = await apiClient.post(`/integrations/${slug}/test-connection`)
  return res.data
}

export interface TestInvoicePayload {
  invoice_id?: string            // reuse to exercise duplicate / retry cases
  supplier_id: string
  amount?: number                // Pula
  currency?: string
  due_at?: string
  percentage_requested?: number
  auto_request_early_payment?: boolean
  force?: boolean                // bypass our local dedupe → hit Swiftly's 422
}

export interface TestInvoiceResult {
  ok: boolean
  invoice_id?: string
  http_status?: number | null
  error?: string | null
  request?: Record<string, unknown>
  response?: unknown
  raw_preview?: string | null
}

export async function submitTestInvoice(slug: string, data: TestInvoicePayload): Promise<TestInvoiceResult> {
  const res = await apiClient.post(`/integrations/${slug}/test-invoice`, data)
  return res.data
}

export interface WebhookEvent {
  id: number
  event_type: string | null
  invoice_id: string | null
  signature_valid: boolean
  status: string
  received_at: string | null
  payload: Record<string, unknown> | null
}

export async function getIntegrationWebhooks(slug: string): Promise<{ data: WebhookEvent[] }> {
  const res = await apiClient.get(`/integrations/${slug}/webhooks`)
  return res.data
}

/** Non-secret config patch. Only the keys sent are written; '' clears one. */
export type IntegrationSettingsPatch = Record<string, string | number | null>

export async function updateIntegrationSettings(
  slug: string,
  data: IntegrationSettingsPatch,
): Promise<{ success: boolean; message: string; changed?: string[]; data: Integration }> {
  const res = await apiClient.put(`/integrations/${slug}/settings`, data)
  return res.data
}

/**
 * Set or replace write-only credentials. There is deliberately no getter:
 * the backend stores these encrypted and never returns them, so the UI can
 * only ever overwrite or clear ('' clears, handing the key back to .env).
 */
export async function updateIntegrationSecrets(
  slug: string,
  data: Record<string, string>,
): Promise<{ success: boolean; message: string; changed?: string[]; data: Integration }> {
  const res = await apiClient.put(`/integrations/${slug}/secrets`, data)
  return res.data
}
